/* ── API client for urblock backend ─────────────────────────── */

const BASE_URL = import.meta.env.VITE_API_URL ?? 'https://api.urblock.io';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly type: string,
    message: string,
    public readonly param?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  error: { type: string; code: string; message: string; param?: string; status: number };
}

/**
 * Auth-aware 401 recovery. The auth store registers a handler that refreshes the
 * access token; request() then transparently retries the original call once with
 * the new token. Registered via setUnauthorizedHandler() to avoid a circular import
 * between the api client and the zustand store.
 */
type UnauthorizedHandler = () => Promise<string | null>;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(fn: UnauthorizedHandler | null): void {
  onUnauthorized = fn;
}

/**
 * Platform-admin secret provider. The /v1/admin/* endpoints are guarded by
 * PlatformAdminGuard which requires the x-admin-secret header (JWT role is NOT
 * sufficient). The admin-secret store registers a provider here so admin requests
 * carry the header automatically without every page threading it through.
 */
let adminSecretProvider: (() => string | null) | null = null;

export function setAdminSecretProvider(fn: (() => string | null) | null): void {
  adminSecretProvider = fn;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null,
  extraHeaders?: Record<string, string>,
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string> | undefined),
    ...extraHeaders,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  /* Platform-admin endpoints need x-admin-secret; inject from the provider unless
     the caller already supplied one explicitly. */
  if (adminSecretProvider && path.startsWith('/v1/admin/') && !headers['x-admin-secret']) {
    const adminSecret = adminSecretProvider();
    if (adminSecret) headers['x-admin-secret'] = adminSecret;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    /* Transparent token refresh: a single retry for expired access tokens on
       authenticated requests (never on the refresh/login endpoints themselves). */
    if (
      res.status === 401 &&
      token &&
      !retried &&
      onUnauthorized &&
      !path.startsWith('/auth/refresh') &&
      !path.startsWith('/auth/login') &&
      !path.startsWith('/auth/unified')
    ) {
      const newToken = await onUnauthorized();
      if (newToken) {
        return request<T>(path, options, newToken, extraHeaders, true);
      }
    }

    let body: ErrorBody | undefined;
    try {
      body = (await res.json()) as ErrorBody;
    } catch {
      /* empty */
    }
    throw new ApiError(
      res.status,
      body?.error?.code ?? 'unknown_error',
      body?.error?.type ?? 'api_error',
      body?.error?.message ?? `Request failed with status ${res.status}`,
      body?.error?.param,
    );
  }

  return (await res.json()) as T;
}

/* ── Auth ─────────────────────────────────────────────────── */

export interface AuthResponse {
  object: 'auth';
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
  expires_in: number;
  tenant?: { object: 'tenant'; id: string; company_name: string; created: number };
  user?: { object: 'user'; id: string; email: string; full_name: string; role: string };
}

export interface SiweNonceResponse {
  object: 'siwe_nonce';
  nonce: string;
}

export interface PasskeyChallengeResponse {
  object: 'passkey_challenge';
  challenge: string;
  rp_id: string;
  timeout_ms: number;
}

export interface MeResponse {
  object: 'user';
  id: string;
  email: string;
  full_name: string;
  role: string;
  tenant: {
    object: 'tenant';
    id: string;
    company_name: string;
    created: number;
    plan: string;
    wallet_mode: 'connect';
    connect_account_address: string | null;
  };
}

export function getSiweNonce(): Promise<SiweNonceResponse> {
  return request<SiweNonceResponse>('/auth/siwe/nonce');
}

export function getPasskeyChallenge(intent: 'register' | 'authenticate'): Promise<PasskeyChallengeResponse> {
  return request<PasskeyChallengeResponse>(`/auth/passkey/challenge?intent=${encodeURIComponent(intent)}`);
}

export function unifiedAuth(data:
  | { method: 'email'; email: string; password: string }
  | { method: 'siwe'; message: string; signature: string; address: string }
  | { method: 'oauth'; provider: 'google' | 'apple'; id_token: string }
  | { method: 'passkey'; flow: 'register'; challenge: string; credential_id: string; client_data_json: string; public_key: { x: string; y: string } }
  | { method: 'passkey'; flow: 'authenticate'; challenge: string; credential_id: string; client_data_json: string; authenticator_data: string; passkey_signature: string }
): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/unified', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function register(data: {
  email: string;
  password: string;
  company_name: string;
  full_name: string;
}): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/register', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function refreshToken(refresh_token: string): Promise<AuthResponse> {
  return request<AuthResponse>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refresh_token }),
  });
}

export function logout(refresh_token: string, token: string): Promise<void> {
  return request('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ refresh_token }),
  }, token);
}

export function getMe(token: string): Promise<MeResponse> {
  return request<MeResponse>('/auth/me', {}, token);
}

export function updateProfile(
  token: string,
  data: { full_name?: string; company_name?: string },
): Promise<MeResponse> {
  return request<MeResponse>('/auth/me', {
    method: 'PATCH',
    body: JSON.stringify(data),
  }, token);
}

/* ── API Keys ────────────────────────────────────────────── */

export interface ApiKeyListItem {
  object: 'api_key';
  id: string;
  name: string;
  secret_prefix: string;
  publishable: string;
  environment: string;
  created: number;
  last_used: number | null;
}

export interface ApiKeyCreateResponse {
  object: 'api_key';
  id: string;
  name: string;
  secret: string;
  publishable: string;
  environment: string;
  created: number;
  last_used: null;
}

export interface ListResponse<T> {
  object: 'list';
  data: T[];
  has_more: boolean;
  total: number;
}

export function listApiKeys(token: string): Promise<ListResponse<ApiKeyListItem>> {
  return request<ListResponse<ApiKeyListItem>>('/v1/api_keys', {}, token);
}

export function createApiKey(
  token: string,
  data: { name: string; environment: 'live' | 'test' },
): Promise<ApiKeyCreateResponse> {
  return request<ApiKeyCreateResponse>('/v1/api_keys', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
}

export function deleteApiKey(token: string, id: string): Promise<void> {
  return request(`/v1/api_keys/${id}`, { method: 'DELETE' }, token);
}

export function rotateApiKey(token: string, id: string): Promise<ApiKeyCreateResponse> {
  return request<ApiKeyCreateResponse>(`/v1/api_keys/${id}/rotate`, { method: 'POST' }, token);
}

/* ── Tokens ──────────────────────────────────────────────── */

export interface TokenListItem {
  object: 'token';
  id: string;
  name: string;
  symbol: string;
  standard: string;
  network: string;
  status: string;
  contract_address: string | null;
  decimals: number;
  paused: boolean;
  created: number;
}

export function listTokens(
  token: string,
  params?: { limit?: number; starting_after?: string; status?: string },
): Promise<ListResponse<TokenListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  if (params?.status) qs.set('status', params.status);
  const query = qs.toString();
  return request<ListResponse<TokenListItem>>(`/v1/tokens${query ? `?${query}` : ''}`, {}, token);
}

/* ── Transactions ────────────────────────────────────────── */

export interface TransactionListItem {
  object: 'transaction';
  id: string;
  type: string;
  status: string;
  token_id: string | null;
  network: string;
  to: string | null;
  amount: string | null;
  tx_hash: string | null;
  block_number: number | null;
  confirmations: number;
  gas_used: string | null;
  created: number;
  confirmed_at: number | null;
}

export function listTransactions(
  token: string,
  params?: { limit?: number; starting_after?: string; status?: string },
): Promise<ListResponse<TransactionListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  if (params?.status) qs.set('status', params.status);
  const query = qs.toString();
  return request<ListResponse<TransactionListItem>>(`/v1/transactions${query ? `?${query}` : ''}`, {}, token);
}

/* ── Networks ────────────────────────────────────────────── */

export interface NetworkResponse {
  object: 'network';
  id: string;
  name: string;
  chain_id: number;
  explorer_url: string;
  is_testnet: boolean;
  is_active: boolean;
}

export function listNetworks(token: string): Promise<ListResponse<NetworkResponse>> {
  return request<ListResponse<NetworkResponse>>('/v1/networks', {}, token);
}

/* ── Token Detail ────────────────────────────────────────── */

export interface TokenDetailResponse {
  object: 'token';
  id: string;
  name: string;
  symbol: string;
  standard: string;
  network: string;
  status: string;
  contract_address: string | null;
  decimals?: number;
  initial_supply?: string | null;
  features: { mintable?: boolean; burnable?: boolean; pausable?: boolean; royalty?: boolean };
  metadata: Record<string, unknown>;
  paused: boolean;
  deploy_tx: string | null;
  admin_transferred: boolean;
  created: number;
}

export function getToken(token: string, id: string): Promise<TokenDetailResponse> {
  return request<TokenDetailResponse>(`/v1/tokens/${id}`, {}, token);
}

/* ── Token Deploy ────────────────────────────────────────── */

export interface CreateTokenInput {
  name: string;
  symbol: string;
  standard: string;
  network: string;
  initial_supply?: string;
  decimals?: number;
  features?: { mintable?: boolean; burnable?: boolean; pausable?: boolean };
  metadata?: Record<string, unknown>;
  base_uri?: string;
}

export function createToken(
  token: string,
  data: CreateTokenInput,
  options?: { environment?: 'test' | 'live' },
): Promise<TokenDetailResponse> {
  return request<TokenDetailResponse>('/v1/tokens', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, options?.environment ? { 'X-Environment': options.environment } : undefined);
}

/* ── Transaction Detail ──────────────────────────────────── */

export interface TransactionDetailResponse {
  object: 'transaction';
  id: string;
  type: string;
  status: string;
  token_id: string | null;
  network: string;
  from: string | null;
  to: string | null;
  amount: string | null;
  tx_hash: string | null;
  block_number: number | null;
  confirmations: number;
  gas_used: string | null;
  gas_price: string | null;
  idempotency_key: string | null;
  error_message: string | null;
  created: number;
  submitted_at: number | null;
  confirmed_at: number | null;
}

export function getTransaction(token: string, id: string): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/transactions/${id}`, {}, token);
}

/* ── Webhooks ────────────────────────────────────────────── */

export interface WebhookEndpointListItem {
  object: 'webhook_endpoint';
  id: string;
  url: string;
  events: string[];
  description: string | null;
  status: string;
  created: number;
}

export interface WebhookEndpointCreateResponse extends WebhookEndpointListItem {
  signing_secret: string;
}

export function listWebhookEndpoints(token: string): Promise<ListResponse<WebhookEndpointListItem>> {
  return request<ListResponse<WebhookEndpointListItem>>('/v1/webhook_endpoints', {}, token);
}

export function createWebhookEndpoint(
  token: string,
  data: { url: string; events: string[]; description?: string },
): Promise<WebhookEndpointCreateResponse> {
  return request<WebhookEndpointCreateResponse>('/v1/webhook_endpoints', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
}

export function updateWebhookEndpoint(
  token: string,
  id: string,
  data: { url?: string; events?: string[]; description?: string },
): Promise<WebhookEndpointListItem> {
  return request<WebhookEndpointListItem>(`/v1/webhook_endpoints/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, token);
}

export function deleteWebhookEndpoint(token: string, id: string): Promise<{ object: 'webhook_endpoint'; id: string; deleted: true }> {
  return request(`/v1/webhook_endpoints/${id}`, { method: 'DELETE' }, token);
}

/* ── Webhook Deliveries ──────────────────────────────────── */

export interface WebhookDeliveryItem {
  object: 'webhook_delivery';
  id: string;
  event_id: string;
  endpoint_id: string;
  status: string;
  http_status: number | null;
  response_body: string | null;
  attempt: number;
  next_retry_at: number | null;
  delivered_at: number | null;
  created: number;
}

export function listWebhookDeliveries(
  token: string,
  endpointId: string,
  params?: { status?: string; limit?: number; starting_after?: string },
): Promise<ListResponse<WebhookDeliveryItem>> {
  const qs = new URLSearchParams();
  if (params?.status) qs.set('status', params.status);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  const query = qs.toString();
  return request<ListResponse<WebhookDeliveryItem>>(
    `/v1/webhook_endpoints/${endpointId}/deliveries${query ? `?${query}` : ''}`,
    {},
    token,
  );
}

export function retryWebhookDelivery(
  token: string,
  deliveryId: string,
): Promise<WebhookDeliveryItem> {
  return request<WebhookDeliveryItem>(`/v1/webhook_deliveries/${deliveryId}/retry`, {
    method: 'POST',
  }, token);
}

/* ── Webhook Events ──────────────────────────────────────── */

export interface WebhookEventItem {
  object: 'event';
  id: string;
  type: string;
  data: Record<string, unknown>;
  created: number;
}

export function listWebhookEvents(
  token: string,
  params?: { type?: string; limit?: number; starting_after?: string },
): Promise<ListResponse<WebhookEventItem>> {
  const qs = new URLSearchParams();
  if (params?.type) qs.set('type', params.type);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  const query = qs.toString();
  return request<ListResponse<WebhookEventItem>>(
    `/v1/webhook_events${query ? `?${query}` : ''}`,
    {},
    token,
  );
}

/* ── Faucet ──────────────────────────────────────────────── */

export interface FaucetNetwork {
  id: string;
  symbol: string;
  drip_amount: string;
  cooldown_hours: number;
  is_active: boolean;
}

export interface FaucetStatusResponse {
  object: 'faucet_status';
  networks: FaucetNetwork[];
}

export interface FaucetDripResponse {
  object: 'faucet_drip';
  id: string;
  network: string;
  wallet_address: string;
  amount: string;
  tx_hash: string | null;
  status: string;
  created_at: string;
}

export function getFaucetStatus(): Promise<FaucetStatusResponse> {
  return request<FaucetStatusResponse>('/v1/faucet/status');
}

export function dripFaucet(
  token: string,
  data: { network: string; wallet_address: string },
): Promise<FaucetDripResponse> {
  return request<FaucetDripResponse>('/v1/faucet/drip', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
}

/* ── Token Operations ────────────────────────────────────── */

export interface MintTokenInput {
  to: string;
  amount?: string;
  token_uri?: string;
  nft_token_id?: string;
  data?: string;
  idempotency_key: string;
}

export interface BurnTokenInput {
  from: string;
  amount?: string;
  nft_token_id?: string;
  idempotency_key: string;
}

export interface TransferTokenInput {
  from: string;
  to: string;
  amount?: string;
  nft_token_id?: string;
  idempotency_key: string;
}

export interface TransferOwnershipInput {
  new_owner: string;
  idempotency_key?: string;
}

export function mintToken(
  token: string,
  tokenId: string,
  data: MintTokenInput,
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/mint`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function burnToken(
  token: string,
  tokenId: string,
  data: BurnTokenInput,
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/burn`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function transferToken(
  token: string,
  tokenId: string,
  data: TransferTokenInput,
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/transfer`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function pauseToken(
  token: string,
  tokenId: string,
  env?: 'test' | 'live',
): Promise<TokenDetailResponse> {
  return request<TokenDetailResponse>(`/v1/tokens/${tokenId}/pause`, {
    method: 'POST',
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function unpauseToken(
  token: string,
  tokenId: string,
  env?: 'test' | 'live',
): Promise<TokenDetailResponse> {
  return request<TokenDetailResponse>(`/v1/tokens/${tokenId}/unpause`, {
    method: 'POST',
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function transferTokenOwnership(
  token: string,
  tokenId: string,
  data: TransferOwnershipInput,
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/transfer_ownership`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── Token Supply & Balance ──────────────────────────────── */

export interface TokenSupplyResponse {
  object: 'token_supply';
  token_id: string;
  total_supply: string;
  total_supply_formatted: string;
  decimals: number;
  network: string;
}

export interface TokenBalanceResponse {
  object: 'token_balance';
  token_id: string;
  nft_token_id: string | null;
  address: string;
  balance: string;
  network: string;
}

export function getTokenSupply(token: string, tokenId: string): Promise<TokenSupplyResponse> {
  return request<TokenSupplyResponse>(`/v1/tokens/${tokenId}/supply`, {}, token);
}

export function getTokenBalance(
  token: string,
  tokenId: string,
  address: string,
  nftTokenId?: string,
): Promise<TokenBalanceResponse> {
  const qs = new URLSearchParams({ address });
  if (nftTokenId) qs.set('nft_token_id', nftTokenId);
  return request<TokenBalanceResponse>(`/v1/tokens/${tokenId}/balance?${qs.toString()}`, {}, token);
}

/* ── Events ──────────────────────────────────────────────── */

export interface EventListItem {
  object: 'event';
  id: string;
  type: string;
  created: number;
  data: Record<string, unknown>;
}

export interface EventDetailResponse {
  object: 'event';
  id: string;
  type: string;
  created: number;
  data: Record<string, unknown>;
  pending_webhooks: number;
}

export function listEvents(
  token: string,
  params?: { limit?: number; type?: string; starting_after?: string },
): Promise<ListResponse<EventListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set('limit', String(params.limit));
  if (params?.type) qs.set('type', params.type);
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  const query = qs.toString();
  return request<ListResponse<EventListItem>>(`/v1/events${query ? `?${query}` : ''}`, {}, token);
}

export function getEvent(token: string, id: string): Promise<EventDetailResponse> {
  return request<EventDetailResponse>(`/v1/events/${id}`, {}, token);
}

/* ── Roles ───────────────────────────────────────────────── */

export interface RolesListResponse {
  object: 'roles_list';
  token_id: string;
  roles: Array<{ role: string; members: string[] }>;
}

export interface AddressRolesResponse {
  object: 'address_roles';
  token_id: string;
  address: string;
  roles: string[];
}

export interface HasRoleResponse {
  object: 'role_check';
  token_id: string;
  role: string;
  address: string;
  has_role: boolean;
}

export function listRoles(token: string, tokenId: string): Promise<RolesListResponse> {
  return request<RolesListResponse>(`/v1/tokens/${tokenId}/roles`, {}, token);
}

export function getAddressRoles(token: string, tokenId: string, address: string): Promise<AddressRolesResponse> {
  return request<AddressRolesResponse>(`/v1/tokens/${tokenId}/roles/${address}`, {}, token);
}

export function checkRole(
  token: string,
  tokenId: string,
  role: string,
  address: string,
): Promise<HasRoleResponse> {
  const qs = new URLSearchParams({ role, address });
  return request<HasRoleResponse>(`/v1/tokens/${tokenId}/roles/check?${qs.toString()}`, {}, token);
}

export function grantRole(
  token: string,
  tokenId: string,
  data: { role: string; address: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/roles`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function revokeRole(
  token: string,
  tokenId: string,
  data: { role: string; address: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/roles`, {
    method: 'DELETE',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── Gas Estimates ───────────────────────────────────────── */

export interface GasEstimateResponse {
  object: 'gas_estimate';
  operation: string;
  gas_limit: string;
  gas_price: string;
  max_fee_per_gas: string;
  max_priority_fee_per_gas: string;
  estimated_cost_wei: string;
  estimated_cost_matic: string;
  network: string;
}

export function getGasEstimate(
  token: string,
  params: { operation: string; token_id?: string; to?: string; amount?: string },
): Promise<GasEstimateResponse> {
  const qs = new URLSearchParams();
  qs.set('operation', params.operation);
  if (params.token_id) qs.set('token_id', params.token_id);
  if (params.to) qs.set('to', params.to);
  if (params.amount) qs.set('amount', params.amount);
  return request<GasEstimateResponse>(`/v1/gas_estimates?${qs.toString()}`, {}, token);
}

/* ── Health / Status ─────────────────────────────────────── */

export interface HealthResponse {
  object: 'health';
  status: 'ok';
  version: string;
  timestamp: number;
}

export interface ServiceStatus {
  status: string;
  latency_ms: number;
  error?: string;
}

export interface StatusResponse {
  object: 'status';
  status: 'operational' | 'degraded';
  services: {
    database: ServiceStatus;
    redis: ServiceStatus;
    polygon_rpc: ServiceStatus;
    polygon_amoy_rpc: ServiceStatus;
  };
  timestamp: string;
}

export function getHealth(): Promise<HealthResponse> {
  return request<HealthResponse>('/health');
}

export function getStatus(token: string): Promise<StatusResponse> {
  return request<StatusResponse>('/v1/status', {}, token);
}

/* ── Vesting ─────────────────────────────────────────────── */

export interface VestingListItem {
  object: 'vesting';
  id: string;
  beneficiary: string;
  token_id: string;
  network: string;
  total_amount: string;
  start_timestamp: number;
  duration_seconds: number;
  cliff_seconds: number;
  contract_address: string | null;
  status: string;
  created: number;
}

export interface VestingDetailResponse extends VestingListItem {
  released_amount: string;
  deploy_tx: string | null;
}

export interface VestedAmountResponse {
  object: 'vested_amount';
  vesting_id: string;
  vested: string;
  timestamp: number;
}

export interface VestingOnChainResponse {
  object: 'vesting_on_chain';
  vesting_id: string;
  beneficiary: string;
  start: number;
  duration: number;
  cliff: number;
  released: string;
  releasable: string;
  total: string;
  token_address: string;
}

export interface CreateVestingInput {
  beneficiary: string;
  token_id: string;
  network: string;
  total_amount: string;
  start_timestamp: number;
  duration_seconds: number;
  cliff_seconds?: number;
  idempotency_key: string;
}

export function listVesting(
  token: string,
  params?: { limit?: number; starting_after?: string },
): Promise<ListResponse<VestingListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  const query = qs.toString();
  return request<ListResponse<VestingListItem>>(`/v1/vesting${query ? `?${query}` : ''}`, {}, token);
}

export function getVesting(token: string, id: string): Promise<VestingDetailResponse> {
  return request<VestingDetailResponse>(`/v1/vesting/${id}`, {}, token);
}

export function getVestedAmount(
  token: string,
  vestingId: string,
  timestamp?: number,
): Promise<VestedAmountResponse> {
  const qs = timestamp ? `?timestamp=${timestamp}` : '';
  return request<VestedAmountResponse>(`/v1/vesting/${vestingId}/vested${qs}`, {}, token);
}

export function getVestingOnChain(token: string, vestingId: string): Promise<VestingOnChainResponse> {
  return request<VestingOnChainResponse>(`/v1/vesting/${vestingId}/on_chain`, {}, token);
}

export function createVesting(
  token: string,
  data: CreateVestingInput,
  env?: 'test' | 'live',
): Promise<VestingDetailResponse> {
  return request<VestingDetailResponse>('/v1/vesting', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function releaseVesting(
  token: string,
  vestingId: string,
  data: { idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/vesting/${vestingId}/release`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── Vaults (ERC-4626) ───────────────────────────────────── */

export interface VaultListItem {
  object: 'vault';
  id: string;
  name: string;
  symbol: string;
  asset_token_id: string;
  network: string;
  contract_address: string | null;
  status: string;
  created: number;
}

export interface VaultDetailResponse extends VaultListItem {
  total_assets: string;
  total_supply: string;
  deploy_tx: string | null;
}

export interface VaultPreviewResponse {
  object: 'vault_preview';
  vault_id: string;
  action: string;
  input_amount: string;
  output_amount: string;
}

export interface VaultLimitsResponse {
  object: 'vault_limits';
  vault_id: string;
  address: string;
  max_deposit: string;
  max_withdraw: string;
  max_mint: string;
  max_redeem: string;
}

export interface CreateVaultInput {
  name: string;
  symbol: string;
  asset_token_id: string;
  network: string;
  idempotency_key: string;
}

export function listVaults(
  token: string,
  params?: { limit?: number; starting_after?: string },
): Promise<ListResponse<VaultListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  const query = qs.toString();
  return request<ListResponse<VaultListItem>>(`/v1/vaults${query ? `?${query}` : ''}`, {}, token);
}

export function getVault(token: string, id: string): Promise<VaultDetailResponse> {
  return request<VaultDetailResponse>(`/v1/vaults/${id}`, {}, token);
}

export function getVaultPreview(
  token: string,
  vaultId: string,
  action: string,
  amount: string,
): Promise<VaultPreviewResponse> {
  const qs = new URLSearchParams({ action, amount });
  return request<VaultPreviewResponse>(`/v1/vaults/${vaultId}/preview?${qs.toString()}`, {}, token);
}

export function getVaultLimits(
  token: string,
  vaultId: string,
  address: string,
): Promise<VaultLimitsResponse> {
  return request<VaultLimitsResponse>(`/v1/vaults/${vaultId}/limits?address=${address}`, {}, token);
}

export function createVault(
  token: string,
  data: CreateVaultInput,
  env?: 'test' | 'live',
): Promise<VaultDetailResponse> {
  return request<VaultDetailResponse>('/v1/vaults', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function depositVault(
  token: string,
  vaultId: string,
  data: { depositor: string; assets: string; receiver?: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/vaults/${vaultId}/deposit`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function withdrawVault(
  token: string,
  vaultId: string,
  data: { owner: string; assets: string; receiver?: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/vaults/${vaultId}/withdraw`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function redeemVault(
  token: string,
  vaultId: string,
  data: { owner: string; shares: string; receiver?: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/vaults/${vaultId}/redeem`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function mintVaultShares(
  token: string,
  vaultId: string,
  data: { minter: string; shares: string; receiver?: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/vaults/${vaultId}/mint`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── Governance ──────────────────────────────────────────── */

export interface GovernanceListItem {
  object: 'governance';
  id: string;
  name: string;
  token_id: string;
  network: string;
  governor_address: string | null;
  timelock_address: string | null;
  status: string;
  created: number;
}

export interface GovernanceDetailResponse extends GovernanceListItem {
  voting_delay: number;
  voting_period: number;
  quorum_numerator: number;
  proposal_threshold: string;
  timelock_delay: number;
  deploy_tx: string | null;
}

export interface GovernanceConfigResponse {
  object: 'governance_config';
  governance_id: string;
  voting_delay: number;
  voting_period: number;
  quorum_numerator: number;
  proposal_threshold: string;
  timelock_min_delay: number;
}

export interface ProposalListItem {
  object: 'proposal';
  id: string;
  governance_id: string;
  proposal_id: string;
  description: string;
  proposer: string;
  status: string;
  created: number;
}

export interface ProposalDetailResponse extends ProposalListItem {
  targets: string[];
  values: string[];
  calldatas: string[];
  start_block: number | null;
  end_block: number | null;
  eta: number | null;
  for_votes: string;
  against_votes: string;
  abstain_votes: string;
  deploy_tx: string | null;
}

export interface ProposalStateResponse {
  object: 'proposal_state';
  proposal_id: string;
  state: string;
  state_label: string;
}

export interface ProposalVotesResponse {
  object: 'proposal_votes';
  proposal_id: string;
  for_votes: string;
  against_votes: string;
  abstain_votes: string;
}

export interface CreateGovernanceInput {
  name: string;
  token_id: string;
  network: string;
  voting_delay: number;
  voting_period: number;
  quorum_numerator: number;
  proposal_threshold: string;
  timelock_delay: number;
  idempotency_key: string;
}

export interface CreateProposalInput {
  description: string;
  targets: string[];
  values: string[];
  calldatas: string[];
  proposer: string;
  idempotency_key: string;
}

export function listGovernance(
  token: string,
  params?: { limit?: number; starting_after?: string },
): Promise<ListResponse<GovernanceListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  const query = qs.toString();
  return request<ListResponse<GovernanceListItem>>(`/v1/governance${query ? `?${query}` : ''}`, {}, token);
}

export function getGovernance(token: string, id: string): Promise<GovernanceDetailResponse> {
  return request<GovernanceDetailResponse>(`/v1/governance/${id}`, {}, token);
}

export function getGovernanceConfig(token: string, id: string): Promise<GovernanceConfigResponse> {
  return request<GovernanceConfigResponse>(`/v1/governance/${id}/config`, {}, token);
}

export function createGovernance(
  token: string,
  data: CreateGovernanceInput,
  env?: 'test' | 'live',
): Promise<GovernanceDetailResponse> {
  return request<GovernanceDetailResponse>('/v1/governance', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function listProposals(
  token: string,
  governanceId: string,
  params?: { limit?: number; starting_after?: string; status?: string },
): Promise<ListResponse<ProposalListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  if (params?.status) qs.set('status', params.status);
  const query = qs.toString();
  return request<ListResponse<ProposalListItem>>(`/v1/governance/${governanceId}/proposals${query ? `?${query}` : ''}`, {}, token);
}

export function getProposal(
  token: string,
  governanceId: string,
  proposalId: string,
): Promise<ProposalDetailResponse> {
  return request<ProposalDetailResponse>(`/v1/governance/${governanceId}/proposals/${proposalId}`, {}, token);
}

export function getProposalState(
  token: string,
  governanceId: string,
  proposalId: string,
): Promise<ProposalStateResponse> {
  return request<ProposalStateResponse>(`/v1/governance/${governanceId}/proposals/${proposalId}/state`, {}, token);
}

export function getProposalVotes(
  token: string,
  governanceId: string,
  proposalId: string,
): Promise<ProposalVotesResponse> {
  return request<ProposalVotesResponse>(`/v1/governance/${governanceId}/proposals/${proposalId}/votes`, {}, token);
}

export function createProposal(
  token: string,
  governanceId: string,
  data: CreateProposalInput,
  env?: 'test' | 'live',
): Promise<ProposalDetailResponse> {
  return request<ProposalDetailResponse>(`/v1/governance/${governanceId}/proposals`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function castVote(
  token: string,
  governanceId: string,
  proposalId: string,
  data: { voter: string; support: number; reason?: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/governance/${governanceId}/proposals/${proposalId}/vote`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function queueProposal(
  token: string,
  governanceId: string,
  proposalId: string,
  data: { idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/governance/${governanceId}/proposals/${proposalId}/queue`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function executeProposal(
  token: string,
  governanceId: string,
  proposalId: string,
  data: { idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/governance/${governanceId}/proposals/${proposalId}/execute`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function cancelProposal(
  token: string,
  governanceId: string,
  proposalId: string,
  data: { idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/governance/${governanceId}/proposals/${proposalId}/cancel`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function delegateVotingPower(
  token: string,
  governanceId: string,
  data: { delegator: string; delegatee: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/governance/${governanceId}/delegate`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── Multisig (Safe{Wallet}) ────────────────────────────── */

export interface MultisigListItem {
  object: 'multisig';
  id: string;
  name: string;
  address: string;
  network: string;
  threshold: number;
  owners: string[];
  status: string;
  created: number;
}

export interface MultisigResponse extends MultisigListItem {
  nonce: number;
  safe_singleton_address: string;
}

export interface MultisigTxListItem {
  object: 'multisig_transaction';
  id: string;
  multisig_id: string;
  tx_index: number;
  to: string;
  value: string;
  data: string;
  status: string;
  confirmations: number;
  required: number;
  created: number;
}

export interface MultisigTxDetailResponse extends MultisigTxListItem {
  tx_hash: string | null;
  executor: string | null;
  executed: number | null;
}

export interface MultisigConfirmationResponse {
  object: 'multisig_confirmation';
  multisig_id: string;
  tx_index: number;
  address: string;
  safe_tx_hash: string;
  is_approved: boolean;
}

export interface MultisigOwnerCheckResponse {
  object: 'multisig_owner_check';
  multisig_id: string;
  address: string;
  is_owner: boolean;
}

export function listMultisigs(
  token: string,
  params?: { network?: string; status?: string },
): Promise<ListResponse<MultisigListItem>> {
  const qs = new URLSearchParams();
  if (params?.network) qs.set('network', params.network);
  if (params?.status) qs.set('status', params.status);
  const q = qs.toString();
  return request<ListResponse<MultisigListItem>>(`/v1/multisigs${q ? `?${q}` : ''}`, {}, token);
}

export function getMultisig(token: string, id: string): Promise<MultisigResponse> {
  return request<MultisigResponse>(`/v1/multisigs/${id}`, {}, token);
}

export function createMultisig(
  token: string,
  data: { name: string; owners: string[]; threshold: number; network: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<MultisigResponse> {
  return request<MultisigResponse>('/v1/multisigs', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function listMultisigTransactions(
  token: string,
  multisigId: string,
): Promise<ListResponse<MultisigTxListItem>> {
  return request<ListResponse<MultisigTxListItem>>(`/v1/multisigs/${multisigId}/transactions`, {}, token);
}

export function getMultisigTransaction(
  token: string,
  multisigId: string,
  txIndex: number,
): Promise<MultisigTxDetailResponse> {
  return request<MultisigTxDetailResponse>(`/v1/multisigs/${multisigId}/transactions/${txIndex}`, {}, token);
}

export function submitMultisigTransaction(
  token: string,
  multisigId: string,
  data: { to: string; value: string; data: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<MultisigTxListItem> {
  return request<MultisigTxListItem>(`/v1/multisigs/${multisigId}/transactions`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function confirmMultisigTransaction(
  token: string,
  multisigId: string,
  txIndex: number,
  data: { signer_address: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/multisigs/${multisigId}/transactions/${txIndex}/confirm`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function executeMultisigTransaction(
  token: string,
  multisigId: string,
  txIndex: number,
  data: { signatures: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/multisigs/${multisigId}/transactions/${txIndex}/execute`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function addMultisigOwner(
  token: string,
  multisigId: string,
  data: { owner: string; threshold: number; signatures: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/multisigs/${multisigId}/owners`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function removeMultisigOwner(
  token: string,
  multisigId: string,
  address: string,
  data: { prev_owner: string; threshold: number; signatures: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/multisigs/${multisigId}/owners/${address}`, {
    method: 'DELETE',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function checkMultisigOwner(
  token: string,
  multisigId: string,
  address: string,
): Promise<MultisigOwnerCheckResponse> {
  return request<MultisigOwnerCheckResponse>(`/v1/multisigs/${multisigId}/owners/${address}`, {}, token);
}

export function changeMultisigThreshold(
  token: string,
  multisigId: string,
  data: { threshold: number; signatures: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/multisigs/${multisigId}/threshold`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── Token Bound Accounts (ERC-6551) ────────────────────── */

export interface TbaListItem {
  object: 'tba';
  id: string;
  token_id: string;
  nft_token_id: string;
  account_address: string;
  network: string;
  status: string;
  created: number;
}

export interface TbaResponse extends TbaListItem {
  implementation_address: string;
  chain_id: number;
}

export interface TbaAccountResponse {
  object: 'tba_account';
  id: string;
  account_address: string;
  implementation: string;
}

export interface TbaTokenResponse {
  object: 'tba_token';
  chain_id: number;
  token_contract: string;
  token_id: string;
}

export interface TbaStateResponse {
  object: 'tba_state';
  state: string;
}

export interface TbaBalanceResponse {
  object: 'tba_balance';
  balance: string;
  balance_formatted: string;
}

export function listTba(
  token: string,
  params?: { limit?: number; starting_after?: string },
): Promise<ListResponse<TbaListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  const q = qs.toString();
  return request<ListResponse<TbaListItem>>(`/v1/tba${q ? `?${q}` : ''}`, {}, token);
}

export function getTba(token: string, id: string): Promise<TbaResponse> {
  return request<TbaResponse>(`/v1/tba/${id}`, {}, token);
}

export function createTba(
  token: string,
  data: { token_id: string; nft_token_id: string; network: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TbaResponse> {
  return request<TbaResponse>('/v1/tba', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function getTbaAccount(token: string, id: string): Promise<TbaAccountResponse> {
  return request<TbaAccountResponse>(`/v1/tba/${id}/account`, {}, token);
}

export function getTbaToken(token: string, id: string): Promise<TbaTokenResponse> {
  return request<TbaTokenResponse>(`/v1/tba/${id}/token`, {}, token);
}

export function getTbaState(token: string, id: string): Promise<TbaStateResponse> {
  return request<TbaStateResponse>(`/v1/tba/${id}/state`, {}, token);
}

export function getTbaBalance(token: string, id: string): Promise<TbaBalanceResponse> {
  return request<TbaBalanceResponse>(`/v1/tba/${id}/balance`, {}, token);
}

export function executeTba(
  token: string,
  id: string,
  data: { to: string; value: string; data: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tba/${id}/execute`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function executeBatchTba(
  token: string,
  id: string,
  data: { calls: { to: string; value: string; data: string }[]; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tba/${id}/execute_batch`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── Chainlink Price Feeds ──────────────────────────────── */

export interface PriceFeedListItem {
  object: 'price_feed';
  pair: string;
  address: string;
  network: string;
  decimals: number;
}

export interface PriceFeedDataResponse {
  object: 'price_feed_data';
  pair: string;
  price: string;
  decimals: number;
  round_id: string;
  updated_at: number;
  answered_in_round: string;
}

export interface PriceRound {
  round_id: string;
  price: string;
  started_at: number;
  updated_at: number;
  answered_in_round: string;
}

export interface PriceFeedConfigResponse {
  object: 'price_feed_config';
  pair: string;
  description: string;
  decimals: number;
  version: number;
  address: string;
}

export interface VrfRequestResponse {
  object: 'vrf_request';
  id: string;
  consumer_id: string;
  request_id: string;
  num_words: number;
  status: string;
  network: string;
  created: number;
}

export interface VrfResultResponse {
  object: 'vrf_result';
  request_id: string;
  random_words: string[];
  fulfilled: boolean;
}

export interface VrfConsumerConfigResponse {
  object: 'vrf_consumer_config';
  id: string;
  key_hash: string;
  subscription_id: string;
  request_confirmations: number;
  callback_gas_limit: number;
}

export interface VrfConsumerDeployResponse {
  object: 'vrf_consumer';
  id: string;
  name: string;
  address: string;
  network: string;
  status: string;
  created: number;
}

export interface PriceFeedConsumerDeployResponse {
  object: 'price_feed_consumer';
  id: string;
  name: string;
  address: string;
  network: string;
  pair: string;
  status: string;
  created: number;
}

export function listPriceFeeds(
  token: string,
  network: string,
): Promise<ListResponse<PriceFeedListItem>> {
  return request<ListResponse<PriceFeedListItem>>(`/v1/price_feeds?network=${encodeURIComponent(network)}`, {}, token);
}

export function getPriceFeed(
  token: string,
  pair: string,
  network: string,
): Promise<PriceFeedDataResponse> {
  return request<PriceFeedDataResponse>(`/v1/price_feeds/${encodeURIComponent(pair)}?network=${encodeURIComponent(network)}`, {}, token);
}

export function getPriceFeedRounds(
  token: string,
  pair: string,
  network: string,
  count?: number,
): Promise<ListResponse<PriceRound>> {
  const qs = new URLSearchParams({ network });
  if (count) qs.set('count', String(count));
  return request<ListResponse<PriceRound>>(`/v1/price_feeds/${encodeURIComponent(pair)}/rounds?${qs}`, {}, token);
}

export function getPriceFeedConfig(
  token: string,
  pair: string,
  network: string,
): Promise<PriceFeedConfigResponse> {
  return request<PriceFeedConfigResponse>(`/v1/price_feeds/${encodeURIComponent(pair)}/config?network=${encodeURIComponent(network)}`, {}, token);
}

export function deployPriceFeedConsumer(
  token: string,
  data: { name: string; network: string; price_feed_address: string; pair: string; max_age_seconds: number; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<PriceFeedConsumerDeployResponse> {
  return request<PriceFeedConsumerDeployResponse>('/v1/price_feeds/consumers', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function createVrfRequest(
  token: string,
  data: { consumer_id: string; num_words: number; network: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<VrfRequestResponse> {
  return request<VrfRequestResponse>('/v1/vrf/requests', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function getVrfRequest(token: string, id: string): Promise<VrfRequestResponse> {
  return request<VrfRequestResponse>(`/v1/vrf/requests/${id}`, {}, token);
}

export function getVrfResult(token: string, id: string): Promise<VrfResultResponse> {
  return request<VrfResultResponse>(`/v1/vrf/requests/${id}/result`, {}, token);
}

export function getVrfConsumerConfig(token: string, id: string): Promise<VrfConsumerConfigResponse> {
  return request<VrfConsumerConfigResponse>(`/v1/vrf/consumers/${id}/config`, {}, token);
}

export function deployVrfConsumer(
  token: string,
  data: { name: string; network: string; vrf_coordinator: string; key_hash: string; subscription_id: string; request_confirmations: number; callback_gas_limit: number; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<VrfConsumerDeployResponse> {
  return request<VrfConsumerDeployResponse>('/v1/vrf/consumers', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── NFT Management ─────────────────────────────────────── */

export interface NftListItem {
  object: 'nft';
  token_id: string;
  nft_token_id: number;
  owner: string;
  token_uri: string;
  created: number;
}

export interface NftDetailResponse extends NftListItem {
  metadata: Record<string, unknown> | null;
}

export interface TokenUriResponse {
  object: 'token_uri';
  token_id: string;
  nft_token_id: string;
  uri: string;
}

export interface ApprovalForAllResponse {
  object: 'approval_for_all';
  owner: string;
  operator: string;
  approved: boolean;
}

export interface RoyaltyInfoResponse {
  object: 'royalty_info';
  receiver: string;
  royalty_amount: string;
}

export function listNfts(
  token: string,
  tokenId: string,
  params?: { limit?: number; starting_after?: string; owner?: string },
): Promise<ListResponse<NftListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  if (params?.owner) qs.set('owner', params.owner);
  const q = qs.toString();
  return request<ListResponse<NftListItem>>(`/v1/tokens/${tokenId}/nfts${q ? `?${q}` : ''}`, {}, token);
}

export function getNft(
  token: string,
  tokenId: string,
  nftTokenId: number,
): Promise<NftDetailResponse> {
  return request<NftDetailResponse>(`/v1/tokens/${tokenId}/nfts/${nftTokenId}`, {}, token);
}

export function getTokenUri(
  token: string,
  tokenId: string,
  nftTokenId: string,
): Promise<TokenUriResponse> {
  return request<TokenUriResponse>(`/v1/tokens/${tokenId}/token_uri?nft_token_id=${encodeURIComponent(nftTokenId)}`, {}, token);
}

export function getApprovalForAll(
  token: string,
  tokenId: string,
  owner: string,
  operator: string,
): Promise<ApprovalForAllResponse> {
  return request<ApprovalForAllResponse>(`/v1/tokens/${tokenId}/approval_for_all?owner=${encodeURIComponent(owner)}&operator=${encodeURIComponent(operator)}`, {}, token);
}

export function getRoyaltyInfo(
  token: string,
  tokenId: string,
  nftTokenId: string,
  salePrice: string,
): Promise<RoyaltyInfoResponse> {
  return request<RoyaltyInfoResponse>(`/v1/tokens/${tokenId}/royalty_info?nft_token_id=${encodeURIComponent(nftTokenId)}&sale_price=${encodeURIComponent(salePrice)}`, {}, token);
}

export function setNftMetadata(
  token: string,
  tokenId: string,
  nftTokenId: number,
  data: { token_uri: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/nfts/${nftTokenId}/metadata`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function setApprovalForAll(
  token: string,
  tokenId: string,
  data: { owner: string; operator: string; approved: boolean; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/set_approval_for_all`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── Token Advanced Operations ──────────────────────────── */

export interface AllowanceResponse {
  object: 'allowance';
  owner: string;
  spender: string;
  allowance: string;
}

export function getAllowance(
  token: string,
  tokenId: string,
  owner: string,
  spender: string,
): Promise<AllowanceResponse> {
  return request<AllowanceResponse>(`/v1/tokens/${tokenId}/allowance?owner=${encodeURIComponent(owner)}&spender=${encodeURIComponent(spender)}`, {}, token);
}

export function approveToken(
  token: string,
  tokenId: string,
  data: { owner: string; spender: string; amount?: string; nft_token_id?: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/approve`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function transferFromToken(
  token: string,
  tokenId: string,
  data: { spender: string; from: string; to: string; amount: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/transfer_from`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function mintBatch(
  token: string,
  tokenId: string,
  data: { to: string; items: { nft_token_id: string; amount: string }[]; data?: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/mint_batch`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function transferBatch(
  token: string,
  tokenId: string,
  data: { from: string; to: string; items: { nft_token_id: string; amount: string }[]; data?: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/transfer_batch`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function burnBatch(
  token: string,
  tokenId: string,
  data: { from: string; items: { nft_token_id: string; amount: string }[]; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/burn_batch`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function setDefaultRoyalty(
  token: string,
  tokenId: string,
  data: { receiver: string; fee_basis_points: number; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/royalty`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function setTokenUri(
  token: string,
  tokenId: string,
  data: { nft_token_id: string; token_uri: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/set_uri`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

export function setBaseUri(
  token: string,
  tokenId: string,
  data: { base_uri: string; idempotency_key: string },
  env?: 'test' | 'live',
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/set_base_uri`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token, env ? { 'X-Environment': env } : undefined);
}

/* ── Billing (Fase 11) ──────────────────────────────────── */

export interface PlanResponse {
  object: 'plan';
  id: string;
  name: string;
  slug: string;
  price_monthly: number;
  price_yearly: number;
  price_usdt_monthly: string;
  price_usdt_yearly: string;
  tx_limit: number;
  rate_limit: number;
  overage_rate: string;
  networks: string;
}

export function listBillingPlans(token: string): Promise<ListResponse<PlanResponse>> {
  return request<ListResponse<PlanResponse>>('/v1/billing/plans', {}, token);
}

export interface SubscriptionResponse {
  object: 'subscription';
  id: string;
  plan: string;
  status: string;
  interval: string;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  stripe_subscription_id: string | null;
  payment_method: string;
  created: number;
}

export interface CryptoCheckoutResponse {
  object: 'crypto_checkout';
  payment_id: string;
  plan: string;
  interval: string;
  amount: string;
  currency: string;
  chain: string;
  wallet_address: string;
  status: string;
  expires_at: number;
}

export interface PaymentResponse {
  object: 'billing_payment';
  id: string;
  tenant_id: string;
  plan: string;
  payment_method: string;
  amount: string;
  currency: string;
  chain: string | null;
  tx_hash: string | null;
  from_address: string | null;
  to_address: string | null;
  status: string;
  interval: string;
  notes: string | null;
  expires_at: number | null;
  confirmed_at: number | null;
  created: number;
}

export interface UsageResponse {
  object: 'usage';
  tenant_id: string;
  plan: string;
  period: string;
  tx_count: number;
  tx_limit: number;
  overage_count: number;
  overage_rate: string;
  rate_limit: number;
}

export interface CheckoutSessionResponse {
  object: 'checkout_session';
  url: string;
  session_id: string;
}

export interface PortalSessionResponse {
  object: 'portal_session';
  url: string;
}

export interface InvoiceItem {
  object: 'invoice';
  id: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: string;
  invoice_pdf: string | null;
  period_start: number;
  period_end: number;
  created: number;
}

export function getSubscription(token: string): Promise<SubscriptionResponse> {
  return request<SubscriptionResponse>('/v1/billing/subscription', {}, token);
}

export function getUsage(token: string): Promise<UsageResponse> {
  return request<UsageResponse>('/v1/billing/usage', {}, token);
}

export function createCheckout(
  token: string,
  data: { plan: string; interval?: string },
): Promise<CheckoutSessionResponse> {
  return request<CheckoutSessionResponse>('/v1/billing/checkout', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
}

export function createPortal(token: string): Promise<PortalSessionResponse> {
  return request<PortalSessionResponse>('/v1/billing/portal', {
    method: 'POST',
    body: JSON.stringify({}),
  }, token);
}

export function listInvoices(
  token: string,
  limit = 10,
): Promise<ListResponse<InvoiceItem>> {
  return request<ListResponse<InvoiceItem>>(`/v1/billing/invoices?limit=${limit}`, {}, token);
}

export function createCryptoCheckout(
  token: string,
  data: { plan: string; interval: string; currency: string; chain: string },
): Promise<CryptoCheckoutResponse> {
  return request<CryptoCheckoutResponse>('/v1/billing/crypto_checkout', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
}

export function getCryptoPaymentStatus(
  token: string,
  paymentId: string,
): Promise<PaymentResponse> {
  return request<PaymentResponse>(`/v1/billing/crypto_checkout/${paymentId}`, {}, token);
}

export function listPayments(
  token: string,
  limit = 20,
  status?: string,
): Promise<ListResponse<PaymentResponse>> {
  const qs = new URLSearchParams({ limit: String(limit) });
  if (status) qs.set('status', status);
  return request<ListResponse<PaymentResponse>>(`/v1/billing/payments?${qs}`, {}, token);
}

/* ── Analytics (Fase 11) ──────────────────────────────────── */

export interface AnalyticsOverviewResponse {
  object: 'analytics_overview';
  tenant_id: string;
  period: string;
  total_transactions: number;
  total_tokens: number;
  total_wallets: number;
  total_webhooks_sent: number;
  tx_by_type: Record<string, number>;
  tx_by_status: Record<string, number>;
  tx_by_day: Array<{ date: string; count: number }>;
}

export interface AnalyticsTransactionsResponse {
  object: 'analytics_transactions';
  period: string;
  total: number;
  by_type: Record<string, number>;
  by_status: Record<string, number>;
  by_network: Record<string, number>;
  by_day: Array<{ date: string; count: number }>;
  avg_gas_used: string;
  avg_confirmation_time_ms: number;
}

export function getAnalyticsOverview(
  token: string,
  period = '30d',
): Promise<AnalyticsOverviewResponse> {
  return request<AnalyticsOverviewResponse>(`/v1/analytics/overview?period=${period}`, {}, token);
}

export function getAnalyticsTransactions(
  token: string,
  period = '30d',
): Promise<AnalyticsTransactionsResponse> {
  return request<AnalyticsTransactionsResponse>(`/v1/analytics/transactions?period=${period}`, {}, token);
}

export interface AnalyticsTokensResponse {
  object: 'analytics_tokens';
  period: string;
  total_tokens: number;
  by_standard: Record<string, number>;
  by_status: Record<string, number>;
  by_network: Record<string, number>;
  recent_deploys: Array<{
    id: string;
    name: string;
    standard: string;
    network: string;
    created: number;
  }>;
}

export interface AnalyticsWebhooksResponse {
  object: 'analytics_webhooks';
  period: string;
  total_sent: number;
  total_delivered: number;
  total_failed: number;
  delivery_rate: number;
  by_event_type: Record<string, number>;
  avg_delivery_time_ms: number;
}

export function getAnalyticsTokens(
  token: string,
  period = '30d',
): Promise<AnalyticsTokensResponse> {
  return request<AnalyticsTokensResponse>(`/v1/analytics/tokens?period=${period}`, {}, token);
}

export function getAnalyticsWebhooks(
  token: string,
  period = '30d',
): Promise<AnalyticsWebhooksResponse> {
  return request<AnalyticsWebhooksResponse>(`/v1/analytics/webhooks?period=${period}`, {}, token);
}

/* ── Admin Billing ───────────────────────────────────────── */

export interface ActivatePlanResponse {
  object: 'plan_activation';
  tenant_id: string;
  plan: string;
  payment_method: string;
  status: string;
  period_start: number;
  period_end: number;
}

export function adminActivatePlan(
  adminSecret: string,
  data: { tenant_id: string; plan: string; interval?: string; notes?: string; duration_days?: number },
): Promise<ActivatePlanResponse> {
  return request<ActivatePlanResponse>('/v1/billing/activate', {
    method: 'POST',
    body: JSON.stringify(data),
  }, null, { 'x-admin-secret': adminSecret });
}

export function adminConfirmPayment(
  adminSecret: string,
  paymentId: string,
  data: { tx_hash?: string; notes?: string },
): Promise<PaymentResponse> {
  return request<PaymentResponse>(`/v1/billing/payments/${paymentId}/confirm`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, null, { 'x-admin-secret': adminSecret });
}

export function adminListPendingPayments(
  adminSecret: string,
  limit = 50,
): Promise<ListResponse<PaymentResponse>> {
  return request<ListResponse<PaymentResponse>>(`/v1/billing/admin/payments?limit=${limit}`, {}, null, { 'x-admin-secret': adminSecret });
}

/* ── Admin Operations Panel ──────────────────────────── */

export interface AdminOverviewResponse {
  object: 'admin_overview';
  total_tenants: number;
  total_users: number;
  total_tokens_deployed: number;
  total_transactions: number;
  total_wallets: number;
  active_subscriptions: number;
  mrr_usd: number;
  tenants_by_plan: Record<string, number>;
  recent_signups: Array<{
    tenant_id: string;
    company_name: string;
    plan: string;
    created_at: string;
  }>;
  pending_payments: number;
}

export interface AdminTenantListItem {
  object: 'admin_tenant';
  id: string;
  company_name: string;
  plan: string;
  owner_email: string | null;
  tokens_count: number;
  transactions_count: number;
  wallets_count: number;
  subscription_status: string | null;
  created_at: string;
}

export interface AdminTenantDetailResponse {
  object: 'admin_tenant_detail';
  id: string;
  company_name: string;
  plan: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  owner: {
    id: string;
    email: string;
    full_name: string;
    role: string;
    created_at: string;
  } | null;
  subscription: {
    plan_id: string;
    status: string;
    interval: string;
    payment_method: string;
    current_period_start: string | null;
    current_period_end: string | null;
  } | null;
  usage: {
    tokens_deployed: number;
    transactions_total: number;
    transactions_this_month: number;
    wallets_created: number;
    api_keys_active: number;
    webhook_endpoints: number;
  };
}

export interface AdminPaymentListItem {
  object: 'admin_payment';
  id: string;
  tenant_id: string;
  company_name: string;
  plan: string;
  payment_method: string;
  amount: string;
  currency: string;
  chain: string | null;
  status: string;
  tx_hash: string | null;
  created_at: string;
  expires_at: string | null;
}

export interface AdminUserListItem {
  object: 'admin_user';
  id: string;
  email: string;
  full_name: string;
  role: string;
  tenant_id: string;
  company_name: string;
  plan: string;
  created_at: string;
}

export interface AdminActivityEvent {
  object: 'activity_event';
  type: string;
  description: string;
  created_at: string;
  metadata: Record<string, unknown>;
}

export function getAdminOverview(token: string): Promise<AdminOverviewResponse> {
  return request<AdminOverviewResponse>('/v1/admin/overview', {}, token);
}

/** Validate a candidate platform-admin secret against the backend (200 = valid, 403 = invalid). */
export async function validateAdminSecret(token: string, adminSecret: string): Promise<boolean> {
  try {
    await request<AdminOverviewResponse>('/v1/admin/overview', {}, token, { 'x-admin-secret': adminSecret });
    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) return false;
    throw err;
  }
}

export function getAdminTenants(
  token: string,
  params: { limit?: number; offset?: number; search?: string; plan?: string; sort?: string; order?: string } = {},
): Promise<ListResponse<AdminTenantListItem>> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.search) qs.set('search', params.search);
  if (params.plan) qs.set('plan', params.plan);
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  return request<ListResponse<AdminTenantListItem>>(`/v1/admin/tenants?${qs.toString()}`, {}, token);
}

export function getAdminTenantDetail(token: string, tenantId: string): Promise<AdminTenantDetailResponse> {
  return request<AdminTenantDetailResponse>(`/v1/admin/tenants/${tenantId}`, {}, token);
}

export function getAdminPayments(
  token: string,
  params: { limit?: number; offset?: number; status?: string; method?: string } = {},
): Promise<ListResponse<AdminPaymentListItem>> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.status) qs.set('status', params.status);
  if (params.method) qs.set('method', params.method);
  return request<ListResponse<AdminPaymentListItem>>(`/v1/admin/payments?${qs.toString()}`, {}, token);
}

export function getAdminUsers(
  token: string,
  params: { limit?: number; offset?: number; search?: string; role?: string } = {},
): Promise<ListResponse<AdminUserListItem>> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.search) qs.set('search', params.search);
  if (params.role) qs.set('role', params.role);
  return request<ListResponse<AdminUserListItem>>(`/v1/admin/users?${qs.toString()}`, {}, token);
}

export function getAdminTenantActivity(
  token: string,
  tenantId: string,
  limit = 20,
): Promise<ListResponse<AdminActivityEvent>> {
  return request<ListResponse<AdminActivityEvent>>(`/v1/admin/tenants/${tenantId}/activity?limit=${limit}`, {}, token);
}

/* ── Studio Leads ─────────────────────────────────────────── */

export interface StudioLeadItem {
  object: 'studio_lead';
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  preferred_contact: string;
  quiz_answers: Record<string, unknown>;
  diagnosis: Record<string, unknown>;
  status: string;
  notes: string | null;
  source: string;
  created_at: string;
  updated_at: string;
}

export interface StudioLeadsStatsResponse {
  object: 'studio_leads_stats';
  total: number;
  by_status: Record<string, number>;
}

export function getAdminLeads(
  token: string,
  params: { limit?: number; offset?: number; status?: string; search?: string; sort?: string; order?: string } = {},
): Promise<ListResponse<StudioLeadItem>> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.status) qs.set('status', params.status);
  if (params.search) qs.set('search', params.search);
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  return request<ListResponse<StudioLeadItem>>(`/v1/leads?${qs.toString()}`, {}, token);
}

export function getAdminLeadDetail(token: string, leadId: string): Promise<StudioLeadItem> {
  return request<StudioLeadItem>(`/v1/leads/${leadId}`, {}, token);
}

export function updateAdminLead(
  token: string,
  leadId: string,
  data: { status?: string; notes?: string },
): Promise<StudioLeadItem> {
  return request<StudioLeadItem>(`/v1/leads/${leadId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }, token);
}

export function deleteAdminLead(token: string, leadId: string): Promise<void> {
  return request<void>(`/v1/leads/${leadId}`, { method: 'DELETE' }, token);
}

export function getAdminLeadsStats(token: string): Promise<StudioLeadsStatsResponse> {
  return request<StudioLeadsStatsResponse>('/v1/leads/stats', {}, token);
}

/* ── Connect (Smart Accounts) ────────────────────────────────── */

export interface ConnectAccountItem {
  object: 'connect_account';
  id: string;
  address: string;
  chain_id: number;
  factory_address: string;
  deployed: boolean;
  signer_type: number;
  created_at: string;
}

export interface ConnectPasskeyItem {
  object: 'connect_passkey';
  id: string;
  credential_id: string;
  public_key: { x: string; y: string };
  algorithm: string;
  created_at: string;
}

export interface ConnectSessionKeyItem {
  object: 'connect_session_key';
  id: string;
  session_key_id: string;
  key: string;
  valid_after: number;
  valid_until: number;
  chain_id: number;
  created_at: string;
}

export interface ConnectRelayOpItem {
  object: 'connect_relay_op';
  id: string;
  user_op_hash: string;
  tx_hash: string | null;
  status: string;
  created_at: string;
}

export interface ConnectPendingOp {
  object: 'connect_pending_op';
  id: string;
  user_op_hash: string;
  sender: string;
  deployed: boolean;
  signer_type: number;
  signer_id: string;
  key_data: string;
  salt: string;
  factory_address: string;
  target_address: string;
  call_data: string;
  target_value: string;
  chain_id: number;
  description: string | null;
  transaction_id: string | null;
  created_at: string;
}

export interface ConnectUserOpPayload {
  sender: string;
  nonce: string;
  init_code: string;
  call_data: string;
  account_gas_limits: string;
  pre_verification_gas: string;
  gas_fees: string;
  paymaster_and_data: string;
  signature: string;
}

export interface ConnectNetworkConfig {
  object: 'connect_network_config';
  chain_id: number;
  factory_address: string | null;
  rpc_url: string;
  bundler_url: string | null;
}

export interface ConnectGasEstimateResponse {
  object: 'connect_gas_estimate';
  pre_verification_gas: string;
  verification_gas_limit: string;
  call_gas_limit: string;
  paymaster_verification_gas_limit: string | null;
  paymaster_post_op_gas_limit: string | null;
}

export interface CreateConnectAccountInput {
  signer_id: string;
  signer_type: number;
  key_data: string;
  salt: string;
  chain_id: number;
}

export interface RegisterConnectPasskeyInput {
  account_address: string;
  credential_id: string;
  public_key: {
    x: string;
    y: string;
  };
  algorithm: string;
  chain_id: number;
}

export function createConnectAccount(
  token: string,
  data: CreateConnectAccountInput,
): Promise<ConnectAccountItem> {
  return request<ConnectAccountItem>('/v1/connect/accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
}

export function registerConnectPasskey(
  token: string,
  data: RegisterConnectPasskeyInput,
): Promise<ConnectPasskeyItem> {
  return request<ConnectPasskeyItem>('/v1/connect/passkeys', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
}

export function listConnectAccounts(
  token: string,
  params: { limit?: number; starting_after?: string } = {},
): Promise<ListResponse<ConnectAccountItem>> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.starting_after) qs.set('starting_after', params.starting_after);
  const q = qs.toString();
  return request<ListResponse<ConnectAccountItem>>(`/v1/connect/accounts${q ? '?' + q : ''}`, {}, token);
}

export function getConnectAccount(
  token: string,
  address: string,
  chainId?: number,
): Promise<ConnectAccountItem> {
  const qs = chainId ? `?chain_id=${chainId}` : '';
  return request<ConnectAccountItem>(`/v1/connect/accounts/${address}${qs}`, {}, token);
}

export function listConnectPasskeys(
  token: string,
  address: string,
): Promise<ConnectPasskeyItem[]> {
  return request<ConnectPasskeyItem[]>(`/v1/connect/accounts/${address}/passkeys`, {}, token);
}

export function listConnectSessionKeys(
  token: string,
  address: string,
): Promise<ConnectSessionKeyItem[]> {
  return request<ConnectSessionKeyItem[]>(`/v1/connect/accounts/${address}/sessions`, {}, token);
}

export function listConnectRelayOps(
  token: string,
  address: string,
): Promise<ConnectRelayOpItem[]> {
  return request<ConnectRelayOpItem[]>(`/v1/connect/accounts/${address}/relay`, {}, token);
}

export function getConnectNetworkConfig(
  token: string,
  chainId: number,
): Promise<ConnectNetworkConfig> {
  return request<ConnectNetworkConfig>(`/v1/connect/networks/${chainId}`, {}, token);
}

/* ── Connect Analytics (Fase 6) ──────────────────────────────── */

export interface ConnectAnalyticsOverview {
  object: 'connect_analytics_overview';
  tenant_id: string;
  period: string;
  total_accounts: number;
  total_deployed: number;
  total_pending: number;
  total_relay_ops: number;
  total_passkeys: number;
  session_keys: { active: number; revoked: number; expired: number };
  recovery: { configured: number; unconfigured: number; active_requests: number };
  accounts_by_chain: Record<string, number>;
  accounts_by_signer_type: Record<string, number>;
  relay_ops_by_status: Record<string, number>;
}

export interface ConnectAnalyticsTimeseries {
  object: 'connect_analytics_timeseries';
  period: string;
  accounts_by_day: Array<{ date: string; count: number }>;
  relay_ops_by_day: Array<{ date: string; count: number }>;
}

export function getConnectAnalyticsOverview(
  token: string,
  period = '30d',
): Promise<ConnectAnalyticsOverview> {
  return request<ConnectAnalyticsOverview>(`/v1/connect/analytics/overview?period=${period}`, {}, token);
}

export function getConnectAnalyticsTimeseries(
  token: string,
  period = '30d',
): Promise<ConnectAnalyticsTimeseries> {
  return request<ConnectAnalyticsTimeseries>(`/v1/connect/analytics/timeseries?period=${period}`, {}, token);
}

/* ── Connect Recovery (Fase 6) ──────────────────────────────── */

export interface ConnectRecoveryGuardian {
  object: 'connect_recovery_guardian';
  id: string;
  guardian_address: string;
  label: string | null;
  created_at: string;
}

export interface ConnectRecoveryRequest {
  object: 'connect_recovery_request';
  id: string;
  validator_address: string | null;
  new_key_data: string;
  confirmations: number;
  required_threshold: number;
  timelock_delay: number;
  ready_at: string | null;
  status: string;
  initiated_by: string | null;
  executed_tx_hash: string | null;
  created_at: string;
}

export interface ConnectRecoveryConfig {
  object: 'connect_recovery_config';
  account_address: string;
  recovery_action_address: string | null;
  guardians: ConnectRecoveryGuardian[];
  threshold: number;
  timelock_delay: number;
  active_requests: ConnectRecoveryRequest[];
}

export interface ConnectSettingsResponse {
  object: 'connect_settings';
  oauth: {
    google_client_id: string | null;
    apple_client_id: string | null;
  };
  passkeys: {
    rp_id: string | null;
    rp_name: string | null;
  };
  cors: {
    allowed_origins: string[];
  };
}

export interface UpdateConnectSettingsInput {
  oauth?: {
    google_client_id?: string | null;
    apple_client_id?: string | null;
  };
  passkeys?: {
    rp_id?: string | null;
    rp_name?: string | null;
  };
  cors?: {
    allowed_origins: string[];
  };
}

export function getConnectRecoveryConfig(
  token: string,
  address: string,
  chainId: number,
): Promise<ConnectRecoveryConfig> {
  return request<ConnectRecoveryConfig>(`/v1/connect/accounts/${address}/recovery?chain_id=${chainId}`, {}, token);
}

export function listConnectPendingOps(token: string): Promise<ConnectPendingOp[]> {
  return request<ConnectPendingOp[]>('/v1/connect/ops/pending', {}, token);
}

export function cancelConnectPendingOp(token: string, userOpHash: string): Promise<{ status: string }> {
  return request<{ status: string }>(`/v1/connect/ops/${userOpHash}/cancel`, { method: 'POST' }, token);
}

export function estimateConnectUserOpGas(
  token: string,
  data: { user_op: ConnectUserOpPayload; chain_id: number },
): Promise<ConnectGasEstimateResponse> {
  return request<ConnectGasEstimateResponse>('/v1/connect/relay/estimate-gas', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
}

export function submitConnectSignedOp(
  token: string,
  userOpHash: string,
  data: {
    signature: string;
    nonce?: string;
    user_op?: ConnectUserOpPayload;
    chain_id?: number;
  },
): Promise<{ user_op_hash: string; status: string }> {
  return request<{ user_op_hash: string; status: string }>(`/v1/connect/ops/${userOpHash}/sign`, {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
}

export function revokeConnectSessionKey(
  token: string,
  address: string,
  sessionKeyId: string,
): Promise<{ status: string }> {
  return request<{ status: string }>(`/v1/connect/accounts/${address}/sessions/${sessionKeyId}`, { method: 'DELETE' }, token);
}

export function deleteConnectPasskey(
  token: string,
  address: string,
  credentialId: string,
): Promise<{ status: string }> {
  return request<{ status: string }>(`/v1/connect/accounts/${address}/passkeys/${encodeURIComponent(credentialId)}`, { method: 'DELETE' }, token);
}

export function getConnectSettings(token: string): Promise<ConnectSettingsResponse> {
  return request<ConnectSettingsResponse>('/v1/settings/connect', {}, token);
}

export function updateConnectSettings(
  token: string,
  data: UpdateConnectSettingsInput,
): Promise<ConnectSettingsResponse> {
  return request<ConnectSettingsResponse>('/v1/settings/connect', {
    method: 'PUT',
    body: JSON.stringify(data),
  }, token);
}

// ==================== Connect OAuth Links ====================

export interface ConnectOAuthLink {
  id: string;
  provider: string;
  provider_user_id: string;
  email?: string | null;
  created_at: string;
}

export function listConnectOAuthLinks(
  token: string | null,
  address: string,
  chainId?: number,
): Promise<ConnectOAuthLink[]> {
  const qs = chainId ? `?chain_id=${chainId}` : '';
  return request<ConnectOAuthLink[]>(`/v1/connect/accounts/${address}/oauth${qs}`, {}, token);
}

export function unlinkConnectOAuth(
  token: string | null,
  address: string,
  provider: string,
  chainId?: number,
): Promise<{ status: string }> {
  const qs = chainId ? `?chain_id=${chainId}` : '';
  return request<{ status: string }>(`/v1/connect/accounts/${address}/oauth/${provider}${qs}`, { method: 'DELETE' }, token);
}

// ==================== Connect Module Check ====================

export interface ConnectModuleCheckResponse {
  address: string;
  module_address: string;
  module_type: number;
  installed?: boolean;
  is_installed?: boolean;
}

export function checkConnectModuleInstalled(
  token: string | null,
  address: string,
  moduleAddress: string,
  moduleType: number,
  chainId?: number,
): Promise<ConnectModuleCheckResponse> {
  const params = new URLSearchParams({
    module_address: moduleAddress,
    module_type: String(moduleType),
  });
  if (chainId) params.set('chain_id', String(chainId));
  return request<ConnectModuleCheckResponse>(
    `/v1/connect/accounts/${address}/modules/installed?${params}`, {}, token,
  );
}

/* ── Identities (T-REX) ─────────────────────────────────── */

export interface IdentityListItem {
  object: 'identity';
  id: string;
  address: string;
  country: number;
  verified: boolean;
  created: number;
}

export interface IdentityDetailResponse {
  object: 'identity';
  id: string;
  address: string;
  identity_address: string;
  country: number;
  network: string;
  status: string;
  verified: boolean;
  created: number;
}

export function listIdentities(
  token: string,
  params?: { limit?: number; starting_after?: string; network?: string },
): Promise<ListResponse<IdentityListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  if (params?.network) qs.set('network', params.network);
  const query = qs.toString();
  return request<ListResponse<IdentityListItem>>(`/v1/identities${query ? `?${query}` : ''}`, {}, token);
}

export function getIdentity(token: string, id: string): Promise<IdentityDetailResponse> {
  return request<IdentityDetailResponse>(`/v1/identities/${id}`, {}, token);
}

export interface CreateIdentityInput {
  address: string;
  identity_address: string;
  country: number;
  network: string;
  identity_registry_address?: string;
  token_id?: string;
  idempotency_key?: string;
}

export function createIdentity(token: string, data: CreateIdentityInput): Promise<IdentityDetailResponse> {
  return request<IdentityDetailResponse>('/v1/identities', { method: 'POST', body: JSON.stringify(data) }, token);
}

export function deleteIdentity(token: string, id: string): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/identities/${id}`, { method: 'DELETE' }, token);
}

/* ── Identity Claims ─────────────────────────────────────── */

export interface ClaimItem {
  object: 'claim';
  id: string;
  topic: number;
  issuer: string;
  status: string;
  created: number;
}

export function listIdentityClaims(
  token: string,
  identityId: string,
  params?: { limit?: number; starting_after?: string },
): Promise<ListResponse<ClaimItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  const query = qs.toString();
  return request<ListResponse<ClaimItem>>(`/v1/identities/${identityId}/claims${query ? `?${query}` : ''}`, {}, token);
}

export function addIdentityClaim(
  token: string,
  identityId: string,
  data: { topic: number; scheme?: number; issuer: string; data?: string; uri?: string; idempotency_key?: string },
): Promise<ClaimItem> {
  return request<ClaimItem>(`/v1/identities/${identityId}/claims`, { method: 'POST', body: JSON.stringify(data) }, token);
}

export function deleteIdentityClaim(token: string, identityId: string, claimId: string): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/identities/${identityId}/claims/${claimId}`, { method: 'DELETE' }, token);
}

/* ── Compliance Modules ──────────────────────────────────── */

export interface ComplianceModuleListItem {
  object: 'compliance_module';
  id: string;
  name: string;
  status: string;
  contract_address: string | null;
  created: number;
}

export interface ComplianceModuleDetailResponse {
  object: 'compliance_module';
  id: string;
  name: string;
  network: string;
  status: string;
  contract_address: string | null;
  rules: { type: string; [key: string]: unknown }[];
  created: number;
}

export function listComplianceModules(
  token: string,
  params?: { limit?: number; starting_after?: string; network?: string },
): Promise<ListResponse<ComplianceModuleListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  if (params?.network) qs.set('network', params.network);
  const query = qs.toString();
  return request<ListResponse<ComplianceModuleListItem>>(`/v1/compliance/modules${query ? `?${query}` : ''}`, {}, token);
}

export function getComplianceModule(token: string, id: string): Promise<ComplianceModuleDetailResponse> {
  return request<ComplianceModuleDetailResponse>(`/v1/compliance/modules/${id}`, {}, token);
}

export interface CreateComplianceModuleInput {
  name: string;
  network: string;
  rules: { type: string; countries?: number[]; allowed?: boolean; max?: string; time_limit?: number; amount_limit?: string }[];
  idempotency_key?: string;
}

export function createComplianceModule(token: string, data: CreateComplianceModuleInput): Promise<ComplianceModuleListItem> {
  return request<ComplianceModuleListItem>('/v1/compliance/modules', { method: 'POST', body: JSON.stringify(data) }, token);
}

export function complianceCheck(
  token: string,
  tokenId: string,
  data: { from: string; to: string; amount: string },
): Promise<{ compliant: boolean; reason?: string }> {
  return request(`/v1/tokens/${tokenId}/compliance/check`, { method: 'POST', body: JSON.stringify(data) }, token);
}

/* ── Trusted Issuers ─────────────────────────────────────── */

export interface TrustedIssuerListItem {
  object: 'trusted_issuer';
  id: string;
  address: string;
  claim_topics: number[];
  status: string;
  created: number;
}

export interface TrustedIssuerDetailResponse extends TrustedIssuerListItem {
  network: string;
}

export function listTrustedIssuers(
  token: string,
  params?: { limit?: number; starting_after?: string; network?: string },
): Promise<ListResponse<TrustedIssuerListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  if (params?.network) qs.set('network', params.network);
  const query = qs.toString();
  return request<ListResponse<TrustedIssuerListItem>>(`/v1/trusted_issuers${query ? `?${query}` : ''}`, {}, token);
}

export function createTrustedIssuer(
  token: string,
  data: { address: string; claim_topics: number[]; network: string; idempotency_key?: string },
): Promise<TrustedIssuerListItem> {
  return request<TrustedIssuerListItem>('/v1/trusted_issuers', { method: 'POST', body: JSON.stringify(data) }, token);
}

export function deleteTrustedIssuer(token: string, id: string): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/trusted_issuers/${id}`, { method: 'DELETE' }, token);
}

/* ── Claim Topics ────────────────────────────────────────── */

export interface ClaimTopicListItem {
  object: 'claim_topic';
  id: string;
  topic: number;
  description: string;
  status: string;
  created: number;
}

export interface ClaimTopicDetailResponse extends ClaimTopicListItem {
  network: string;
}

export function listClaimTopics(
  token: string,
  params?: { limit?: number; starting_after?: string; network?: string },
): Promise<ListResponse<ClaimTopicListItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  if (params?.network) qs.set('network', params.network);
  const query = qs.toString();
  return request<ListResponse<ClaimTopicListItem>>(`/v1/claim_topics${query ? `?${query}` : ''}`, {}, token);
}

export function createClaimTopic(
  token: string,
  data: { topic: number; description: string; network: string; idempotency_key?: string },
): Promise<ClaimTopicListItem> {
  return request<ClaimTopicListItem>('/v1/claim_topics', { method: 'POST', body: JSON.stringify(data) }, token);
}

export function deleteClaimTopic(token: string, topicId: string): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/claim_topics/${topicId}`, { method: 'DELETE' }, token);
}

/* ── T-REX Token Operations ──────────────────────────────── */

export interface FrozenAddressItem {
  address: string;
  frozen_at: number;
}

export function listFrozenAddresses(
  token: string,
  tokenId: string,
  params?: { limit?: number; starting_after?: string },
): Promise<ListResponse<FrozenAddressItem>> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.starting_after) qs.set('starting_after', params.starting_after);
  const query = qs.toString();
  return request<ListResponse<FrozenAddressItem>>(`/v1/tokens/${tokenId}/frozen${query ? `?${query}` : ''}`, {}, token);
}

export function freezeAddress(
  token: string,
  tokenId: string,
  data: { address: string; idempotency_key: string },
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/freeze`, { method: 'POST', body: JSON.stringify(data) }, token);
}

export function unfreezeAddress(
  token: string,
  tokenId: string,
  data: { address: string; idempotency_key: string },
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/unfreeze`, { method: 'POST', body: JSON.stringify(data) }, token);
}

export function forceTransfer(
  token: string,
  tokenId: string,
  data: { from: string; to: string; amount: string; idempotency_key: string },
): Promise<TransactionDetailResponse> {
  return request<TransactionDetailResponse>(`/v1/tokens/${tokenId}/force_transfer`, { method: 'POST', body: JSON.stringify(data) }, token);
}

export function getTokenIdentityRegistry(token: string, tokenId: string): Promise<{ address: string }> {
  return request(`/v1/tokens/${tokenId}/identity_registry`, {}, token);
}

export function getTokenComplianceInfo(token: string, tokenId: string): Promise<{ address: string; modules: string[] }> {
  return request(`/v1/tokens/${tokenId}/compliance_info`, {}, token);
}
